import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Screen } from '@/components/Screen'
import { Kicker } from '@/components/Kicker'
import { TextInput } from '@/components/Field'
import { SkeletonList } from '@/components/Skeleton'
import { supabase } from '@/lib/supabase'
import { useAppStore } from '@/stores/appStore'
import { absoluteDate } from '@/lib/format'
import type { EventRow, JSONValue } from '@/lib/types'

/**
 * SearchScreen — Cmd+K style global search across notes, needs, and people.
 * Results grouped by type, tap to open. Client-side ILIKE via PostgREST —
 * no migration, no tsvector, no LLM. Simple + fast for the data sizes
 * BabyOS is at.
 */

interface NeedHit { id: string; title: string; status: string; urgency: string }
interface PersonHit { id: string; display_name: string; person_type: string }

const DEBOUNCE_MS = 200

export function SearchScreen() {
  const nav = useNavigate()
  const familyId = useAppStore((s) => s.familyId)
  const [q, setQ] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [notes, setNotes] = useState<EventRow[]>([])
  const [needs, setNeeds] = useState<NeedHit[]>([])
  const [persons, setPersons] = useState<PersonHit[]>([])

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [q])

  useEffect(() => {
    if (!familyId || debouncedQ.length < 2) {
      setNotes([]); setNeeds([]); setPersons([])
      return
    }
    let cancelled = false
    void (async () => {
      setBusy(true); setErr(null)
      try {
        // Prefer full-text tsvector search (migration 20260909090000). If the search_text
        // column isn't present on this DB yet, fall back to ILIKE.
        const q = debouncedQ
        const [notesRes, needsRes, personsRes] = await Promise.all([
          supabase.from('events').select('*').eq('family_id', familyId)
            .eq('event_type', 'note.created')
            .textSearch('search_text', q, { type: 'websearch', config: 'simple' })
            .limit(25),
          supabase.from('needs').select('id, title, status, urgency').eq('family_id', familyId)
            .textSearch('search_text', q, { type: 'websearch', config: 'simple' })
            .limit(25),
          supabase.from('persons').select('id, display_name, person_type').eq('family_id', familyId)
            .neq('identity_status', 'archived')
            .ilike('display_name', `%${q.replace(/[%_]/g, '\\$&')}%`).limit(25),
        ])
        // If tsvector column doesn't exist yet (migration not applied), retry with ILIKE
        if (notesRes.error?.message?.includes('search_text') || needsRes.error?.message?.includes('search_text')) {
          const pattern = `%${q.replace(/[%_]/g, '\\$&')}%`
          const [n, d] = await Promise.all([
            supabase.from('events').select('*').eq('family_id', familyId).eq('event_type', 'note.created').ilike('payload->>text', pattern).limit(25),
            supabase.from('needs').select('id, title, status, urgency').eq('family_id', familyId).ilike('title', pattern).limit(25),
          ])
          notesRes.data = n.data; notesRes.error = n.error
          needsRes.data = d.data; needsRes.error = d.error
        }
        if (cancelled) return
        setNotes((notesRes.data ?? []) as EventRow[])
        setNeeds((needsRes.data ?? []) as NeedHit[])
        setPersons((personsRes.data ?? []) as PersonHit[])
      } catch (e) {
        if (cancelled) return
        setErr(e instanceof Error ? e.message : String(e))
      } finally {
        if (!cancelled) setBusy(false)
      }
    })()
    return () => { cancelled = true }
  }, [debouncedQ, familyId])

  const totalHits = notes.length + needs.length + persons.length
  const noneFound = debouncedQ.length >= 2 && !busy && totalHits === 0

  const heroPlaceholder = useMemo(() =>
    ['first smile', 'poop', 'formula', 'nap in the crib', 'Kellyanne', 'growth spurt'][Math.floor(Math.random() * 6)],
    [])

  return (
    <Screen title="Search">
      <div className="px-4 pt-3 pb-6 space-y-4">
        <div className="glass-tile p-4">
          <Kicker>Search everything</Kicker>
          <div className="mt-3">
            <TextInput
              type="search"
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={`Try "${heroPlaceholder}"`}
            />
          </div>
          <div className="mt-1 text-tag text-text-tertiary">
            Notes · Needs · People. Type at least 2 characters.
          </div>
        </div>

        {err && (
          <div className="rounded-r-sm p-3 text-caption" style={{ background: 'rgba(232,94,110,0.12)', color: '#D14758' }}>
            {err}
          </div>
        )}

        {busy && <SkeletonList rows={3} />}

        {noneFound && (
          <div className="glass-tile p-6 text-center">
            <div className="text-body text-text-secondary">Nothing found for "{debouncedQ}".</div>
            <div className="mt-1 text-caption text-text-tertiary">Try shorter words or different phrasing.</div>
          </div>
        )}

        <AnimatePresence>
          {persons.length > 0 && (
            <motion.section initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
              <Kicker>People</Kicker>
              <div className="mt-2 space-y-2">
                {persons.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => nav(`/person/${p.id}`)}
                    className="glass-tile p-3 w-full text-left"
                  >
                    <div className="text-body text-text-primary">{p.display_name}</div>
                    <div className="text-caption text-text-tertiary mt-0.5">{p.person_type}</div>
                  </button>
                ))}
              </div>
            </motion.section>
          )}

          {needs.length > 0 && (
            <motion.section initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
              <Kicker>Needs</Kicker>
              <div className="mt-2 space-y-2">
                {needs.map((n) => (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => nav(`/needs/${n.id}`)}
                    className="glass-tile p-3 w-full text-left"
                  >
                    <div className="text-body text-text-primary truncate">{n.title}</div>
                    <div className="text-caption text-text-tertiary mt-0.5">{n.urgency} · {n.status}</div>
                  </button>
                ))}
              </div>
            </motion.section>
          )}

          {notes.length > 0 && (
            <motion.section initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
              <Kicker>Notes</Kicker>
              <div className="mt-2 space-y-2">
                {notes.map((e) => {
                  const text = extractNoteText(e.payload)
                  return (
                    <button
                      key={e.id}
                      type="button"
                      onClick={() => nav('/timeline')}
                      className="glass-tile p-3 w-full text-left"
                    >
                      <div className="text-body text-text-primary line-clamp-2">{text}</div>
                      <div className="text-caption text-text-tertiary mt-0.5">{absoluteDate(e.occurred_at)}</div>
                    </button>
                  )
                })}
              </div>
            </motion.section>
          )}
        </AnimatePresence>
      </div>
    </Screen>
  )
}

function extractNoteText(payload: JSONValue): string {
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    const t = (payload as Record<string, unknown>).text
    if (typeof t === 'string') return t
  }
  return ''
}
